import React, { useEffect, useMemo, useState } from 'react';
import { PromoterCard } from '../../components/PromoterCard';
import { SubscriptionList } from '../../components/SubscriptionList';
import { VenueCard } from '../../components/VenueCard';
import { useToast } from '../../contexts/ToastContext';
import {
  useNotificationSettings,
  usePromoters,
  useRequestPromoter,
  useRequestVenue,
  useSendWeatherReportNow,
  useSetWeatherReportSubscription,
  useSubscribe,
  useSubscriptions,
  useUnsubscribe,
  useUpdateNotificationSettings,
  useVenues,
  useWeatherReportSubscription,
} from '../../hooks/useApi';
import { trackEvent } from '../../services/analytics';
import { Promoter, Subscription, Venue } from '../../types';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { PageShell } from '../components/PageShell';
import { Panel } from '../components/Panel';
import { SegmentedControl } from '../components/SegmentedControl';
import { Skeleton, SkeletonCard } from '../components/Skeleton';
import { StatTile, StatTileRow } from '../components/StatTile';
import { CheckIcon, ChevronDownIcon, PlusIcon, SearchIcon } from '../components/icons';
import { cx, FOCUS_RING, FOCUS_RING_SURFACE } from '../components/styles';

/**
 * NotificationsPage → `/v2/app/notifications` (DESIGN_PLAN §3.5).
 *
 * All nine `useApi` hooks, `useToast`, all seven `trackEvent` calls, every
 * mutation handler and every derived memo are v1's, verbatim. Only layout,
 * defaults and disclosure are new.
 *
 * The layout answers one question: *where does the user act, and where is the
 * thing they manage?* Both are now the same place —
 *
 *  - `lg`+: browse grid (8 cols) with a persistent, sticky **Watching** rail
 *    (4 cols) beside it. Subscribing and unsubscribing share one viewport.
 *  - below `lg`: the rail collapses into a single stack directly above the
 *    browse controls, its list height-capped, so the subscriptions are one
 *    thumb-scroll from the grid rather than a page away.
 *  - subscribed entities are ringed and tagged **in the browse grid itself**,
 *    plus filterable (All / Subscribed / Not subscribed), so the two lists
 *    never have to be cross-referenced.
 *
 * Consequently there is no floating "My Subscriptions" scroll-to button, and
 * no `showFloatingBtn` state or `IntersectionObserver` to drive one: nothing
 * is far enough away to need a jump.
 *
 * Notification settings are a set-once task, not a browsing task, so they live
 * in their own collapsed strip at the top with a live status summary.
 *
 * View preferences (tab, browse filter, both disclosures) persist to
 * `localStorage`.
 *
 * `VenueCard`, `PromoterCard` and `SubscriptionList` are imported from
 * `src/components/` unedited.
 */

const STORE_PREFIX = 'flashtools.v2.notifications.';
const KEY_TAB = `${STORE_PREFIX}tab`;
const KEY_FILTER = `${STORE_PREFIX}browseFilter`;
const KEY_SETTINGS_OPEN = `${STORE_PREFIX}settingsOpen`;
const KEY_WATCHING_OPEN = `${STORE_PREFIX}watchingOpen`;

/** The Flash Forecast email's "Turn off weather reports" link lands here. */
const FLASH_FORECAST_HASH = '#flash-forecast';
const FLASH_FORECAST_ROW_ID = 'flash-forecast';

/**
 * Remembered view preference. Layout state only — never anything the server
 * owns. Falls back silently when storage is unavailable (private mode).
 */
function usePersistentState<T extends string | boolean>(
  key: string,
  fallback: T,
  isValid?: (value: T) => boolean,
) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return fallback;
      const parsed = (typeof fallback === 'boolean' ? raw === 'true' : raw) as unknown as T;
      return isValid && !isValid(parsed) ? fallback : parsed;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, String(value));
    } catch {
      /* storage unavailable — the preference is session-only */
    }
  }, [key, value]);

  return [value, setValue] as const;
}

type BrowseFilter = 'all' | 'subscribed' | 'available';

const isBrowseFilter = (value: BrowseFilter) =>
  value === 'all' || value === 'subscribed' || value === 'available';

/** v1's inline switch, unchanged pixels, plus the `role="switch"` §6 asks for. */
const SettingSwitch: React.FC<{
  checked: boolean;
  label: string;
  onToggle: () => void;
}> = ({ checked, label, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={onToggle}
    className={cx('relative inline-block h-6 w-11 flex-none cursor-pointer rounded-full', FOCUS_RING_SURFACE)}
  >
    <span
      className={cx(
        'block h-6 w-11 rounded-full border transition-colors',
        checked ? 'border-emerald-500/30 bg-emerald-500/20' : 'border-white/10 bg-white/5',
      )}
    />
    <span
      className={cx(
        'absolute top-1 h-4 w-4 rounded-full shadow-sm transition-all',
        checked ? 'right-1 bg-emerald-400' : 'left-1 bg-white/40',
      )}
    />
  </button>
);

const SettingRow: React.FC<{
  id?: string;
  icon?: React.ReactNode;
  title: string;
  badge?: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
  indent?: boolean;
  /** Extra content under the description, e.g. a secondary action. */
  children?: React.ReactNode;
}> = ({ id, icon, title, badge, description, checked, onToggle, indent, children }) => (
  <div id={id} className={cx('flex scroll-mt-24 items-center justify-between gap-4 p-5', indent && 'sm:pl-14')}>
    <div className="flex min-w-0 flex-1 items-center gap-4">
      {icon && (
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10">
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="flex items-center gap-2 text-sm font-medium text-white">
          {title}
          {badge && (
            <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
              {badge}
            </span>
          )}
        </h3>
        <p className="mt-0.5 text-xs text-white/40">{description}</p>
        {children}
      </div>
    </div>
    <SettingSwitch checked={checked} label={title} onToggle={onToggle} />
  </div>
);

/**
 * Subscribed state, made legible in the browse grid without touching
 * `VenueCard` / `PromoterCard`: a tinted ring plus a tag on the top edge.
 * Venues tint blue, promoters violet.
 */
const BrowseCardFrame: React.FC<{
  subscribed: boolean;
  tone: 'blue' | 'violet';
  children: React.ReactNode;
}> = ({ subscribed, tone, children }) => (
  <div
    className={cx(
      'relative rounded-2xl',
      subscribed && (tone === 'blue' ? 'ring-1 ring-blue-500/40' : 'ring-1 ring-violet-500/40'),
    )}
  >
    {subscribed && (
      <span
        className={cx(
          'absolute -top-2 left-4 z-10 inline-flex items-center gap-1 rounded-full border bg-[#0f0f0f]',
          'px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
          tone === 'blue'
            ? 'border-blue-500/30 text-blue-300'
            : 'border-violet-500/30 text-violet-300',
        )}
      >
        <CheckIcon className="h-3 w-3" />
        Subscribed
      </span>
    )}
    {children}
  </div>
);

const inputClass = cx(
  'w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white',
  'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
  FOCUS_RING_SURFACE,
);

const modalLabelClass =
  'mb-2 block text-xs font-medium uppercase tracking-wider text-white/40';

/** How many venues / promoters the browse grid shows before "Show all". */
const COLLAPSED_COUNT = 12;

const selectClass = (active: boolean) =>
  cx(
    'rounded-lg border px-3 py-1.5 text-xs font-medium transition-all focus:outline-none',
    active ? 'border-white/20 bg-white/10 text-white' : 'border-white/10 bg-white/5 text-white/40 hover:text-white/60',
    FOCUS_RING,
  );

type Rankable = {
  id: number;
  name: string;
  popularity_rank?: number;
  is_popular?: boolean;
  capacity?: number | null;
};

/**
 * Most popular first: the backend's popularity_rank when it sends one, then
 * the popular endpoint's order, then the is_popular flag, then capacity
 * (venues only), then name.
 */
const byPopularity = (popularRank: Map<number, number>) => (a: Rankable, b: Rankable) => {
  const backendRank = (a.popularity_rank ?? Infinity) - (b.popularity_rank ?? Infinity);
  if (backendRank) return backendRank;
  const rank = (popularRank.get(a.id) ?? Infinity) - (popularRank.get(b.id) ?? Infinity);
  if (rank) return rank;
  if (!!a.is_popular !== !!b.is_popular) return a.is_popular ? -1 : 1;
  const capacity = (b.capacity ?? 0) - (a.capacity ?? 0);
  if (capacity) return capacity;
  return a.name.localeCompare(b.name);
};

const filterChipClass = (active: boolean) =>
  cx(
    'rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
    active ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40 hover:text-white/60',
    FOCUS_RING,
  );

export const NotificationsScreen: React.FC = () => {
  const { showToast, showSuccess, showError } = useToast();
  const { data: settings } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();
  const [emailDraft, setEmailDraft] = useState('');
  const [emailDirty, setEmailDirty] = useState(false);

  // Sync email draft from server
  useEffect(() => {
    if (settings && !emailDirty) {
      setEmailDraft(settings.notification_email || '');
    }
  }, [settings, emailDirty]);

  const { data: venues = [], isLoading: venuesLoading } = useVenues();
  const { data: popularVenues = [], isLoading: popularVenuesLoading } = useVenues({ popular: true, limit: 10 });
  const { data: promoters = [], isLoading: promotersLoading } = usePromoters();
  const { data: popularPromoters = [], isLoading: popularPromotersLoading } = usePromoters({ popular: true, limit: 10 });
  const { data: subscriptions = [], isLoading: subsLoading } = useSubscriptions();

  const [venueQuery, setVenueQuery] = useState('');
  const [promoterQuery, setPromoterQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [activeTab, setActiveTab] = usePersistentState<'venues' | 'promoters'>(
    KEY_TAB,
    'venues',
    (tab) => tab === 'venues' || tab === 'promoters',
  );

  /* View preferences — layout only, remembered between visits. */
  const [browseFilter, setBrowseFilter] = usePersistentState<BrowseFilter>(KEY_FILTER, 'all', isBrowseFilter);
  const [settingsOpen, setSettingsOpen] = usePersistentState<boolean>(KEY_SETTINGS_OPEN, false);
  const { data: weatherReport } = useWeatherReportSubscription();
  const setWeatherReport = useSetWeatherReportSubscription();
  const sendWeatherReport = useSendWeatherReportNow();

  // Arriving from the Flash Forecast email: open settings, then bring the row
  // (or the panel, when email is off and the row is hidden) into view once.
  const [pendingForecastScroll, setPendingForecastScroll] = useState(
    () => window.location.hash === FLASH_FORECAST_HASH,
  );
  useEffect(() => {
    if (pendingForecastScroll) setSettingsOpen(true);
  }, [pendingForecastScroll, setSettingsOpen]);
  useEffect(() => {
    if (!pendingForecastScroll || !settings || !settingsOpen) return;
    const target =
      document.getElementById(FLASH_FORECAST_ROW_ID) ?? document.getElementById('v2-notification-settings');
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPendingForecastScroll(false);
  }, [pendingForecastScroll, settings, settingsOpen]);
  const [watchingOpen, setWatchingOpen] = usePersistentState<boolean>(KEY_WATCHING_OPEN, true);

  const [showRequestVenue, setShowRequestVenue] = useState(false);
  const [showRequestPromoter, setShowRequestPromoter] = useState(false);
  const [requestVenueName, setRequestVenueName] = useState('');
  const [requestVenueCity, setRequestVenueCity] = useState('');
  const [requestVenueNotes, setRequestVenueNotes] = useState('');
  const [requestVenuePlatform, setRequestVenuePlatform] = useState('');
  const [requestVenueUrl, setRequestVenueUrl] = useState('');
  const [requestPromoterName, setRequestPromoterName] = useState('');
  const [requestPromoterNotes, setRequestPromoterNotes] = useState('');
  const [requestPromoterPlatform, setRequestPromoterPlatform] = useState('');
  const [requestPromoterUrl, setRequestPromoterUrl] = useState('');

  const loading = venuesLoading || promotersLoading || subsLoading || popularVenuesLoading || popularPromotersLoading;

  useEffect(() => { trackEvent('notifications_page_viewed'); }, []);

  const subscribeMutation = useSubscribe();
  const unsubscribeMutation = useUnsubscribe();
  const requestVenueMutation = useRequestVenue();
  const requestPromoterMutation = useRequestPromoter();

  const handleSubscribeVenue = async (venue: Venue) => {
    await subscribeMutation.mutateAsync({ type: 'venue', entityId: String(venue.id) });
    trackEvent('venue_subscribed', { venueId: String(venue.id), venueName: venue.name });
  };

  const handleSubscribePromoter = async (promoter: Promoter) => {
    await subscribeMutation.mutateAsync({ type: 'promoter', entityId: String(promoter.id) });
    trackEvent('promoter_subscribed', { promoterId: String(promoter.id), promoterName: promoter.name });
  };

  const handleUnsubscribe = async (sub: Subscription) => {

    await unsubscribeMutation.mutateAsync({ id: sub.id, type: sub.type });
    trackEvent('subscription_removed', { subscriptionId: String(sub.id), type: sub.type });

  };

  const handleRequestVenue = async () => {
    if (!requestVenueName.trim()) return;
    try {
      await requestVenueMutation.mutateAsync({
        name: requestVenueName,
        city: requestVenueCity || undefined,
        notes: requestVenueNotes || undefined,
        platform: requestVenuePlatform || undefined,
        url: requestVenueUrl || undefined,
      });
      showSuccess('Venue request submitted!');
      trackEvent('venue_requested', { venueName: requestVenueName, city: requestVenueCity || '' });
      setShowRequestVenue(false);
      setRequestVenueName('');
      setRequestVenueCity('');
      setRequestVenueNotes('');
      setRequestVenuePlatform('');
      setRequestVenueUrl('');
    } catch (error: any) {
      showError(error?.message || 'Failed to submit request');
    }
  };

  const handleRequestPromoter = async () => {
    if (!requestPromoterName.trim()) return;
    try {
      await requestPromoterMutation.mutateAsync({
        name: requestPromoterName,
        notes: requestPromoterNotes || undefined,
        platform: requestPromoterPlatform || undefined,
        url: requestPromoterUrl || undefined,
      });
      showSuccess('Promoter request submitted!');
      trackEvent('promoter_requested', { promoterName: requestPromoterName });
      setShowRequestPromoter(false);
      setRequestPromoterName('');
      setRequestPromoterNotes('');
      setRequestPromoterPlatform('');
      setRequestPromoterUrl('');
    } catch (error: any) {
      showError(error?.message || 'Failed to submit request');
    }
  };

  // Every city once, A–Z, with how many venues it has.
  const cities = useMemo(() => {
    const counts = new Map<string, number>();
    venues.forEach((v) => {
      const city = v.city?.trim();
      if (city) counts.set(city, (counts.get(city) ?? 0) + 1);
    });
    return Array.from(counts, ([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [venues]);

  const popularVenueRank = useMemo(
    () => new Map(popularVenues.map((v, i) => [v.id, i])),
    [popularVenues],
  );
  const popularPromoterRank = useMemo(
    () => new Map(popularPromoters.map((p, i) => [p.id, i])),
    [popularPromoters],
  );

  const venuesWithSubscription = useMemo(() => venues.map((venue) => ({
    ...venue,
    is_subscribed: subscriptions.some(
      (sub) => sub.type === 'venue' && (sub.entity_id === venue.venue_id || sub.entity_id === String(venue.id))
    ),
  })), [venues, subscriptions]);

  const promotersWithSubscription = useMemo(() => promoters.map((promoter) => ({
    ...promoter,
    is_subscribed: subscriptions.some(
      (sub) => sub.type === 'promoter' && (sub.entity_id === promoter.promoter_id || sub.entity_id === String(promoter.id))
    ),
  })), [promoters, subscriptions]);

  const filteredVenues = useMemo(() => venuesWithSubscription.filter((v) => {
    const matchesQuery = v.name.toLowerCase().includes(venueQuery.toLowerCase());
    const matchesCity = !cityFilter || v.city?.trim() === cityFilter;
    return matchesQuery && matchesCity;
  }).sort(byPopularity(popularVenueRank)), [venuesWithSubscription, venueQuery, cityFilter, popularVenueRank]);

  const filteredPromoters = useMemo(() => promotersWithSubscription.filter((p) =>
    p.name.toLowerCase().includes(promoterQuery.toLowerCase())
  ).sort(byPopularity(popularPromoterRank)), [promotersWithSubscription, promoterQuery, popularPromoterRank]);

  const venueSubscriptions = subscriptions.filter(s => s.type === 'venue');
  const promoterSubscriptions = subscriptions.filter(s => s.type === 'promoter');

  /* ------------------------------------------------------------------ */
  /* Layout only below this line.                                        */
  /* ------------------------------------------------------------------ */

  /** Subscribed / not-subscribed view of the already-searched list. */
  const visibleVenues = useMemo(
    () => filteredVenues.filter((v) =>
      browseFilter === 'all' ? true : browseFilter === 'subscribed' ? v.is_subscribed : !v.is_subscribed,
    ),
    [filteredVenues, browseFilter],
  );

  const visiblePromoters = useMemo(
    () => filteredPromoters.filter((p) =>
      browseFilter === 'all' ? true : browseFilter === 'subscribed' ? p.is_subscribed : !p.is_subscribed,
    ),
    [filteredPromoters, browseFilter],
  );

  // Collapsed to the most popular until the seller narrows the list or asks
  // for all of it. Search, a city or "Subscribed" always shows every match.
  const [venuesExpanded, setVenuesExpanded] = useState(false);
  const [promotersExpanded, setPromotersExpanded] = useState(false);
  const venuesCollapsible = !venueQuery && !cityFilter && browseFilter !== 'subscribed'
    && visibleVenues.length > COLLAPSED_COUNT;
  const promotersCollapsible = !promoterQuery && browseFilter !== 'subscribed'
    && visiblePromoters.length > COLLAPSED_COUNT;
  const shownVenues = venuesCollapsible && !venuesExpanded ? visibleVenues.slice(0, COLLAPSED_COUNT) : visibleVenues;
  const shownPromoters = promotersCollapsible && !promotersExpanded
    ? visiblePromoters.slice(0, COLLAPSED_COUNT)
    : visiblePromoters;

  const venueSubscribedCount = filteredVenues.filter((v) => v.is_subscribed).length;
  const promoterSubscribedCount = filteredPromoters.filter((p) => p.is_subscribed).length;

  const browseCounts = activeTab === 'venues'
    ? { all: filteredVenues.length, subscribed: venueSubscribedCount, available: filteredVenues.length - venueSubscribedCount }
    : { all: filteredPromoters.length, subscribed: promoterSubscribedCount, available: filteredPromoters.length - promoterSubscribedCount };

  const cardGridClass = 'grid gap-x-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2';

  const watchingSummary =
    `${venueSubscriptions.length} venue${venueSubscriptions.length !== 1 ? 's' : ''}` +
    ` · ${promoterSubscriptions.length} promoter${promoterSubscriptions.length !== 1 ? 's' : ''}`;

  const browseFilterOptions: { value: BrowseFilter; label: string; count: number }[] = [
    { value: 'all', label: 'All', count: browseCounts.all },
    { value: 'subscribed', label: 'Subscribed', count: browseCounts.subscribed },
    { value: 'available', label: 'Not subscribed', count: browseCounts.available },
  ];

  const browseFilterRow = (extra?: React.ReactNode) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-1 text-xs text-white/30">Show:</span>
      {browseFilterOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={browseFilter === option.value}
          onClick={() => setBrowseFilter(option.value)}
          className={filterChipClass(browseFilter === option.value)}
        >
          {option.label}
          <span className="ml-1.5 tabular-nums text-white/30">{option.count}</span>
        </button>
      ))}
      {extra}
    </div>
  );

  return (
    <PageShell>
      <PageHeader
        title="Notifications"
        subtitle="Get alerts when new events drop from your favorite venues and promoters"
      />

      {/* ── Settings: their own home, collapsed by default ───────────── */}
      {settings && (
        <Panel padding="none" className="mb-6" tourId="notifications-settings">
          <button
            type="button"
            onClick={() => setSettingsOpen(!settingsOpen)}
            aria-expanded={settingsOpen}
            aria-controls="v2-notification-settings"
            className={cx(
              'flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.02]',
              settingsOpen ? 'rounded-t-2xl' : 'rounded-2xl',
              FOCUS_RING,
            )}
          >
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-white">Notification settings</h2>
              <p className="mt-0.5 text-xs text-white/40">Choose how and where you hear about new events.</p>
            </div>
            <div className="flex flex-none items-center gap-3">
              <span
                className={cx(
                  'hidden rounded-full border px-2 py-0.5 text-[11px] font-medium sm:inline-block',
                  settings.email_notifications_enabled
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                    : 'border-white/10 bg-white/5 text-white/40',
                )}
              >
                {settings.email_notifications_enabled
                  ? `Email on${settings.notification_email ? ` · ${settings.notification_email}` : ''}`
                  : 'Email off'}
              </span>
              <ChevronDownIcon
                className={cx('h-4 w-4 text-white/40 transition-transform', settingsOpen && 'rotate-180')}
              />
            </div>
          </button>

          {settingsOpen && (
            <div id="v2-notification-settings" className="divide-y divide-white/[0.06] border-t border-white/[0.06]">
              {/* Email Notifications (parent toggle) */}
              <SettingRow
                icon={
                  <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
                title="Email Notifications"
                description="Receive email alerts"
                checked={settings.email_notifications_enabled}
                onToggle={() => {
                  const next = !settings.email_notifications_enabled;
                  updateSettings.mutate({ email_notifications_enabled: next }, {
                    onSuccess: () => showSuccess(next ? 'Email notifications enabled' : 'Email notifications disabled'),
                    onError: () => showError('Failed to update settings'),
                  });
                }}
              />

              {/* Sub-settings (only shown when email is enabled) */}
              {settings.email_notifications_enabled && (
                <>
                  {/* New Event Notifications */}
                  <SettingRow
                    indent
                    title="New Event Alerts"
                    description="Get alerted when new events drop"
                    checked={settings.new_event_notifications_enabled}
                    onToggle={() => {
                      const next = !settings.new_event_notifications_enabled;
                      updateSettings.mutate({ new_event_notifications_enabled: next }, {
                        onSuccess: () => showSuccess(next ? 'New event alerts enabled' : 'New event alerts disabled'),
                        onError: () => showError('Failed to update settings'),
                      });
                    }}
                  />

                  {/* Ticket Change Notifications */}
                  <SettingRow
                    indent
                    title="Ticket Change Alerts"
                    description="Get alerted when ticket status changes"
                    checked={settings.ticket_change_notifications_enabled}
                    onToggle={() => {
                      const next = !settings.ticket_change_notifications_enabled;
                      updateSettings.mutate({ ticket_change_notifications_enabled: next }, {
                        onSuccess: () => showSuccess(next ? 'Ticket change alerts enabled' : 'Ticket change alerts disabled'),
                        onError: () => showError('Failed to update settings'),
                      });
                    }}
                  />

                  {/* Flash Forecast (weather email; its own subscription endpoint) */}
                  <SettingRow
                    id={FLASH_FORECAST_ROW_ID}
                    indent
                    title="Flash Forecast"
                    badge="New"
                    description="Weather for your upcoming shows · Mon, Thu & Fri at 8 AM ET"
                    checked={weatherReport?.subscribed ?? false}
                    onToggle={() => {
                      // A double tap would otherwise send PUT then DELETE.
                      if (setWeatherReport.isPending) return;
                      const next = !(weatherReport?.subscribed ?? false);
                      setWeatherReport.mutate(next, {
                        onSuccess: () => showSuccess(next
                          ? 'Flash Forecast on. Your first one arrives the next Mon, Thu or Fri at 8 AM ET.'
                          : 'Flash Forecast off'),
                        onError: () => showError("Couldn't update Flash Forecast. Try again."),
                      });
                    }}
                  >
                    <button
                      type="button"
                      disabled={sendWeatherReport.isPending}
                      onClick={() => {
                        sendWeatherReport.mutate(undefined, {
                          onSuccess: ({ sent, message }) => showToast(message, sent ? 'success' : 'info'),
                          onError: (error: any) => showError(error?.status === 429
                            ? 'A Flash Forecast was just sent. Try again in a few minutes.'
                            : "Couldn't send Flash Forecast. Try again."),
                        });
                      }}
                      className={cx(
                        'mt-2 rounded text-xs font-medium text-blue-400 transition-colors hover:text-blue-300',
                        'disabled:cursor-not-allowed disabled:text-white/30',
                        FOCUS_RING_SURFACE,
                      )}
                    >
                      {sendWeatherReport.isPending ? 'Sending…' : 'Email me a forecast now'}
                    </button>
                  </SettingRow>

                  {/* Notification Email */}
                  <div className="p-5 sm:pl-14">
                    <label htmlFor="v2-notification-email" className="mb-1 block text-sm font-medium text-white">
                      Where to send alerts
                    </label>
                    <p className="mb-2 text-xs text-white/40">The email address we'll send your notifications to.</p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        id="v2-notification-email"
                        type="email"
                        value={emailDraft}
                        onChange={e => { setEmailDraft(e.target.value); setEmailDirty(true); }}
                        placeholder="you@example.com"
                        className={cx(
                          'min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white',
                          'placeholder-white/30 transition-colors focus:border-[#3b82f6]/50 focus:outline-none',
                          FOCUS_RING_SURFACE,
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          updateSettings.mutate({ notification_email: emailDraft.trim() }, {
                            onSuccess: () => { setEmailDirty(false); showSuccess('Notification email updated'); },
                            onError: () => showError('Failed to update email'),
                          });
                        }}
                        disabled={updateSettings.isPending || !emailDirty || emailDraft === (settings.notification_email || '')}
                        className={cx(
                          'flex-none rounded-lg bg-[#3b82f6] px-4 py-2 text-sm font-medium text-white transition-colors',
                          'hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-30',
                          FOCUS_RING_SURFACE,
                        )}
                      >
                        {updateSettings.isPending ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Panel>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* ── Watching: the list the user manages, beside the browse grid
             at lg and directly above it below lg. ───────────────────── */}
        <aside className="min-w-0 lg:order-2 lg:col-span-4 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:pr-1">
          <Panel padding="none">
            <div className="flex items-start gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-white">Watching</h2>
                <p className="mt-0.5 text-xs text-white/40">{watchingSummary}</p>
              </div>
              <button
                type="button"
                onClick={() => setWatchingOpen(!watchingOpen)}
                aria-expanded={watchingOpen}
                aria-controls="v2-watching-list"
                className={cx(
                  'flex flex-none items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-white/50',
                  'transition-colors hover:text-white',
                  FOCUS_RING_SURFACE,
                )}
              >
                {watchingOpen ? 'Hide' : 'Show'}
                <ChevronDownIcon
                  className={cx('h-4 w-4 transition-transform', watchingOpen && 'rotate-180')}
                />
              </button>
            </div>

            {watchingOpen && (
              <div id="v2-watching-list" className="border-t border-white/[0.06] p-5">
                {subsLoading ? (
                  <div className="space-y-3" aria-busy="true" aria-label="Loading...">
                    <Skeleton className="h-16 w-full rounded-xl" />
                    <Skeleton className="h-16 w-full rounded-xl" />
                  </div>
                ) : (
                  <>
                    {subscriptions.length > 0 && (
                      <StatTileRow cols={2} className="mb-5">
                        <StatTile
                          label={`Venue${venueSubscriptions.length !== 1 ? 's' : ''}`}
                          value={venueSubscriptions.length}
                          color="blue"
                        />
                        <StatTile
                          label={`Promoter${promoterSubscriptions.length !== 1 ? 's' : ''}`}
                          value={promoterSubscriptions.length}
                        />
                      </StatTileRow>
                    )}

                    <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/30">
                      My Subscriptions
                    </h3>
                    {/* Height-capped below lg so the browse grid stays within
                        reach; the whole rail scrolls at lg instead. */}
                    <div className="max-h-[22rem] overflow-y-auto pr-1 lg:max-h-none lg:overflow-visible lg:pr-0">
                      <SubscriptionList
                        subscriptions={subscriptions}
                        onUnsubscribe={handleUnsubscribe}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </Panel>
        </aside>

        {/* ── Browse & subscribe ─────────────────────────────────────── */}
        <div className="min-w-0 space-y-6 lg:order-1 lg:col-span-8">
          {/* Tabs */}
          <SegmentedControl<'venues' | 'promoters'>
            label="Entity type"
            tourId="notifications-browse"
            value={activeTab}
            onChange={(tab) => {
              setActiveTab(tab);
              trackEvent('notifications_tab_switched', { tab });
            }}
            options={[
              { value: 'venues', label: 'Venues' },
              { value: 'promoters', label: 'Promoters' },
            ]}
          />

          {loading ? (
            <div className="space-y-4" aria-busy="true" aria-label="Loading...">
              <Skeleton className="h-11 w-full rounded-xl" />
              <div className={cardGridClass}>
                {Array.from({ length: 6 }, (_, i) => (
                  <SkeletonCard key={i} className="mb-4" />
                ))}
              </div>
            </div>
          ) : activeTab === 'venues' ? (
            <div className="space-y-4">
              {/* Search and Filter */}
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative min-w-0 flex-1">
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                  <input
                    type="text"
                    placeholder="Search venues..."
                    aria-label="Search venues"
                    value={venueQuery}
                    onChange={(e) => setVenueQuery(e.target.value)}
                    className={cx(
                      'w-full rounded-xl border border-white/10 bg-white/5 py-2.5 pl-10 pr-4 text-sm text-white',
                      'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
                      FOCUS_RING,
                    )}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowRequestVenue(true)}
                  className={cx(
                    'flex flex-none items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5',
                    'px-4 py-2.5 text-sm text-white/60 transition-all hover:bg-white/10 hover:text-white',
                    FOCUS_RING,
                  )}
                >
                  <PlusIcon className="h-4 w-4" />
                  Request Venue
                </button>
              </div>

              {/* Subscribed / not-subscribed view, plus city */}
              {browseFilterRow(cities.length > 0 && (
                <select
                  aria-label="City"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className={cx('sm:ml-2', selectClass(!!cityFilter))}
                >
                  <option value="">All cities</option>
                  {cities.map(({ name, count }) => (
                    <option key={name} value={name}>{name} ({count})</option>
                  ))}
                </select>
              ))}

              {/* Venues List */}
              <div className={cardGridClass}>
                {shownVenues.map((venue) => (
                  <BrowseCardFrame key={venue.id} subscribed={!!venue.is_subscribed} tone="blue">
                    <VenueCard
                      venue={venue}
                      onSubscribe={handleSubscribeVenue}
                    />
                  </BrowseCardFrame>
                ))}
              </div>

              {/* Collapsed: most popular first, the rest one tap away */}
              {venuesCollapsible && (
                <button
                  type="button"
                  onClick={() => setVenuesExpanded(!venuesExpanded)}
                  className={cx(
                    'w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white/60',
                    'transition-all hover:bg-white/10 hover:text-white',
                    FOCUS_RING,
                  )}
                >
                  {venuesExpanded ? 'Show fewer' : `Show all ${visibleVenues.length} venues`}
                </button>
              )}

              {!filteredVenues.length && venueQuery && (
                <EmptyState
                  title={`No venues found for "${venueQuery}"`}
                  action={
                    <button
                      type="button"
                      onClick={() => {
                        setRequestVenueName(venueQuery);
                        setShowRequestVenue(true);
                      }}
                      className={cx('rounded text-sm text-blue-400 transition-colors hover:text-blue-300', FOCUS_RING)}
                    >
                      Request this venue →
                    </button>
                  }
                />
              )}

              {!!filteredVenues.length && !visibleVenues.length && (
                <EmptyState
                  title={browseFilter === 'subscribed' ? 'No subscribed venues here' : 'You already subscribe to every venue here'}
                  action={
                    <button
                      type="button"
                      onClick={() => setBrowseFilter('all')}
                      className={cx('rounded text-sm text-blue-400 transition-colors hover:text-blue-300', FOCUS_RING)}
                    >
                      Show all venues →
                    </button>
                  }
                />
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search */}
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative min-w-0 flex-1">
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                  <input
                    type="text"
                    placeholder="Search promoters..."
                    aria-label="Search promoters"
                    value={promoterQuery}
                    onChange={(e) => setPromoterQuery(e.target.value)}
                    className={cx(
                      'w-full rounded-xl border border-white/10 bg-white/5 py-2.5 pl-10 pr-4 text-sm text-white',
                      'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
                      FOCUS_RING,
                    )}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowRequestPromoter(true)}
                  className={cx(
                    'flex flex-none items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5',
                    'px-4 py-2.5 text-sm text-white/60 transition-all hover:bg-white/10 hover:text-white',
                    FOCUS_RING,
                  )}
                >
                  <PlusIcon className="h-4 w-4" />
                  Request Promoter
                </button>
              </div>

              {/* Subscribed / not-subscribed view */}
              {browseFilterRow()}

              {/* Promoters List */}
              <div className={cardGridClass}>
                {shownPromoters.map((promoter) => (
                  <BrowseCardFrame key={promoter.id} subscribed={!!promoter.is_subscribed} tone="violet">
                    <PromoterCard
                      promoter={promoter}
                      onSubscribe={handleSubscribePromoter}
                    />
                  </BrowseCardFrame>
                ))}
              </div>

              {/* Collapsed: most popular first, the rest one tap away */}
              {promotersCollapsible && (
                <button
                  type="button"
                  onClick={() => setPromotersExpanded(!promotersExpanded)}
                  className={cx(
                    'w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white/60',
                    'transition-all hover:bg-white/10 hover:text-white',
                    FOCUS_RING,
                  )}
                >
                  {promotersExpanded ? 'Show fewer' : `Show all ${visiblePromoters.length} promoters`}
                </button>
              )}

              {!filteredPromoters.length && promoterQuery && (
                <EmptyState
                  title={`No promoters found for "${promoterQuery}"`}
                  action={
                    <button
                      type="button"
                      onClick={() => {
                        setRequestPromoterName(promoterQuery);
                        setShowRequestPromoter(true);
                      }}
                      className={cx('rounded text-sm text-blue-400 transition-colors hover:text-blue-300', FOCUS_RING)}
                    >
                      Request this promoter →
                    </button>
                  }
                />
              )}

              {!!filteredPromoters.length && !visiblePromoters.length && (
                <EmptyState
                  title={browseFilter === 'subscribed' ? 'No subscribed promoters here' : 'You already subscribe to every promoter here'}
                  action={
                    <button
                      type="button"
                      onClick={() => setBrowseFilter('all')}
                      className={cx('rounded text-sm text-blue-400 transition-colors hover:text-blue-300', FOCUS_RING)}
                    >
                      Show all promoters →
                    </button>
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Request Venue Modal */}
      {showRequestVenue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="v2-request-venue-title"
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f0f] p-6"
          >
            <h2 id="v2-request-venue-title" className="mb-1 text-lg font-medium text-white">Request a Venue</h2>
            <p className="mb-6 text-sm text-white/40">Can't find a venue? Let us know and we'll add it.</p>

            <div className="space-y-4">
              <div>
                <label htmlFor="v2-rv-name" className={modalLabelClass}>Venue Name</label>
                <input
                  id="v2-rv-name"
                  type="text"
                  placeholder="Enter venue name"
                  value={requestVenueName}
                  onChange={(e) => setRequestVenueName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="v2-rv-city" className={modalLabelClass}>City (optional)</label>
                <input
                  id="v2-rv-city"
                  type="text"
                  placeholder="Enter city"
                  value={requestVenueCity}
                  onChange={(e) => setRequestVenueCity(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="v2-rv-platform" className={modalLabelClass}>Platform (optional)</label>
                <select
                  id="v2-rv-platform"
                  value={requestVenuePlatform}
                  onChange={(e) => setRequestVenuePlatform(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select platform...</option>
                  <option value="DICE">DICE</option>
                  <option value="AXS">AXS</option>
                </select>
              </div>
              <div>
                <label htmlFor="v2-rv-url" className={modalLabelClass}>URL (optional)</label>
                <input
                  id="v2-rv-url"
                  type="url"
                  placeholder="https://dice.fm/venue/..."
                  value={requestVenueUrl}
                  onChange={(e) => setRequestVenueUrl(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="v2-rv-notes" className={modalLabelClass}>Notes (optional)</label>
                <textarea
                  id="v2-rv-notes"
                  placeholder="Any additional info..."
                  value={requestVenueNotes}
                  onChange={(e) => setRequestVenueNotes(e.target.value)}
                  rows={3}
                  className={cx(inputClass, 'resize-none')}
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowRequestVenue(false)}
                className={cx('flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-white/60 transition-colors hover:text-white', FOCUS_RING_SURFACE)}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRequestVenue}
                disabled={!requestVenueName.trim() || requestVenueMutation.isPending}
                className={cx(
                  'flex-1 rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors',
                  'hover:bg-white/90 disabled:opacity-50',
                  FOCUS_RING_SURFACE,
                )}
              >
                {requestVenueMutation.isPending ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Promoter Modal */}
      {showRequestPromoter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="v2-request-promoter-title"
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f0f] p-6"
          >
            <h2 id="v2-request-promoter-title" className="mb-1 text-lg font-medium text-white">Request a Promoter</h2>
            <p className="mb-6 text-sm text-white/40">Can't find a promoter? Let us know and we'll add them.</p>

            <div className="space-y-4">
              <div>
                <label htmlFor="v2-rp-name" className={modalLabelClass}>Promoter Name</label>
                <input
                  id="v2-rp-name"
                  type="text"
                  placeholder="Enter promoter name"
                  value={requestPromoterName}
                  onChange={(e) => setRequestPromoterName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="v2-rp-platform" className={modalLabelClass}>Platform (optional)</label>
                <select
                  id="v2-rp-platform"
                  value={requestPromoterPlatform}
                  onChange={(e) => setRequestPromoterPlatform(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select platform...</option>
                  <option value="DICE">DICE</option>
                  <option value="AXS">AXS</option>
                </select>
              </div>
              <div>
                <label htmlFor="v2-rp-url" className={modalLabelClass}>URL (optional)</label>
                <input
                  id="v2-rp-url"
                  type="url"
                  placeholder="https://dice.fm/promoter/..."
                  value={requestPromoterUrl}
                  onChange={(e) => setRequestPromoterUrl(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="v2-rp-notes" className={modalLabelClass}>Notes (optional)</label>
                <textarea
                  id="v2-rp-notes"
                  placeholder="Any additional info..."
                  value={requestPromoterNotes}
                  onChange={(e) => setRequestPromoterNotes(e.target.value)}
                  rows={3}
                  className={cx(inputClass, 'resize-none')}
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowRequestPromoter(false)}
                className={cx('flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-white/60 transition-colors hover:text-white', FOCUS_RING_SURFACE)}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRequestPromoter}
                disabled={!requestPromoterName.trim() || requestPromoterMutation.isPending}
                className={cx(
                  'flex-1 rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors',
                  'hover:bg-white/90 disabled:opacity-50',
                  FOCUS_RING_SURFACE,
                )}
              >
                {requestPromoterMutation.isPending ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};
